import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { HtmlTooltip } from '../../../components/Tooltip/HtmlTooltip'

export type ReportType = 'default' | 'withAddress' | 'split' | 'expectedRelease';
export type FileType = 'xls' | 'csv';

export interface ExportDialogProps {
  // Controle do tipo de relatório (controlado pelo pai)
  reportType: ReportType
  setReportType: React.Dispatch<React.SetStateAction<ReportType>>

  // Se false, a opção "split" não aparece (ex: só em Aprovadas)
  allowSplit?: boolean

  // Valores default (opcionais)
  defaultFileType?: FileType

  // Dispara o download (pai decide controller/endpoint)
  onExport: (args: { reportType: ReportType; fileType: FileType }) => void | Promise<void>

  // UI
  buttonText?: string
  disabled?: boolean
  allowExpectedRelease?: boolean
}

export function ExportDialog({
  reportType,
  setReportType,
  allowSplit = false,
  defaultFileType = 'xls',
  onExport,
  buttonText = 'Exportar',
  disabled = false,
  allowExpectedRelease = false,
}: ExportDialogProps) {
  const [open, setOpen] = useState(false)
  const [fileType, setFileType] = useState<FileType>(defaultFileType)
  const [isExporting, setIsExporting] = useState(false)

  // Garante consistência caso alguém tente manter "split" quando não permitido
  React.useEffect(() => {
    if (!allowSplit && reportType === 'split') {
      setReportType('default')
    }

    if (!allowExpectedRelease && reportType === 'expectedRelease') {
      setReportType('default')
    }
  }, [allowSplit, allowExpectedRelease, reportType, setReportType])

  const reportHelperText = useMemo(() => {
    switch (reportType) {
      case 'default':
        return 'Gera o relatório padrão com as colunas atuais.'
      case 'withAddress':
        return 'Adiciona colunas do endereço do cliente ao relatório.'
      case 'split':
        return 'Gera o relatório com split (linhas detalhadas por transação/itens).'
      case 'expectedRelease':
        return 'Gera o relatório com previsão de liberação (Disponível agora, A liberar e Data prevista).'
      default:
        return ''
    }
  }, [reportType])

  const handleOpen = () => {
    if (disabled) return
    setOpen(true)
  }

  const handleClose = () => {
    if (isExporting) return
    setOpen(false)
  }

  const handleExport = async () => {
    try {
      setIsExporting(true)
      await onExport({ reportType, fileType })
      setOpen(false)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Box sx={{ marginLeft: 'auto' }}>
      <Button
        onClick={handleOpen}
        disabled={disabled}
        endIcon={
          <img
            src="/assets/icons/downloads/download.svg"
            alt="download-icon-sells"
          />
        }
        sx={{ textTransform: 'none' }}
      >
        <Typography
          color="rgba(99, 126, 239, 1)"
          fontFamily={'Montserrat'}
          fontSize={'14px'}
          lineHeight={'18px'}
          fontWeight={500}
        >
          {buttonText}
        </Typography>
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Exportar relatório</DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            {/* Tipo de relatório */}
            <FormControl>
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                Tipo de relatório
              </Typography>

              <RadioGroup
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
              >
                <FormControlLabel
                  value="default"
                  control={<Radio />}
                  label="Padrão"
                />

                <FormControlLabel
                    value="withAddress"
                    control={<Radio />}
                    label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography>Padrão + endereço do cliente</Typography>
                        <HtmlTooltip title="Os dados do endereço do cliente serão adicionados no relatório">
                            <img
                            src="/assets/icons/infos/info.svg"
                            alt="tooltip-info"
                            style={{ cursor: 'pointer' }}
                            />
                        </HtmlTooltip>
                        </Stack>
                    }
                    />


                {allowSplit && (
                  <FormControlLabel
                    value="split"
                    control={<Radio />}
                    label="Split por transação"
                  />
                )}

                {allowExpectedRelease && (
                  <FormControlLabel
                    value="expectedRelease"
                    control={<Radio />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography>Previsão de liberação</Typography>
                        <HtmlTooltip title="Adiciona colunas: Disponível agora, A liberar e Previsão de liberação.">
                          <img
                            src="/assets/icons/infos/info.svg"
                            alt="tooltip-info"
                            style={{ cursor: 'pointer' }}
                          />
                        </HtmlTooltip>
                      </Stack>
                    }
                  />
                )}

              </RadioGroup>

              <Typography variant="body2" color="text.secondary">
                {reportHelperText}
              </Typography>
            </FormControl>

            <Divider />

            {/* Formato */}
            <FormControl>
              <Typography fontWeight={600} sx={{ mb: 1 }}>
                Formato do arquivo
              </Typography>

              <RadioGroup
                row
                value={fileType}
                onChange={(e) => setFileType(e.target.value as FileType)}
              >
                <FormControlLabel value="xls" control={<Radio />} label="Excel" />
                <FormControlLabel value="csv" control={<Radio />} label="CSV" />
              </RadioGroup>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={isExporting}>
            Cancelar
          </Button>

          <Button
            onClick={handleExport}
            disabled={isExporting}
            variant="contained"
          >
            {isExporting ? 'Gerando…' : 'Baixar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
